/**
 * Grouping and labelling workouts by calendar day. Shared by the history list and
 * the activity feed so the two genuinely match rather than drifting apart.
 */

/** Parses "YYYY-MM-DD" as a local date. */
function parseDate(workoutDate: string): Date {
  const [year, month, day] = workoutDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * "Tue, Aug 4", or "Tue, Aug 4, 2025" outside the current year.
 *
 * The date is parsed from its parts rather than passed to `new Date(string)`,
 * which treats a date-only ISO string as UTC midnight and so renders the day
 * before in any zone behind UTC.
 */
export function formatDayHeading(workoutDate: string, today: Date = new Date()): string {
  const date = parseDate(workoutDate);
  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export interface DayGroup<T> {
  date: string;
  items: T[];
}

/**
 * Runs of consecutive items sharing a date, in the order given - so the caller's
 * sort decides whether days come newest or oldest first.
 */
export function groupByDay<T extends { workoutDate: string }>(items: T[]): DayGroup<T>[] {
  const groups: DayGroup<T>[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.date === item.workoutDate) last.items.push(item);
    else groups.push({ date: item.workoutDate, items: [item] });
  }
  return groups;
}
